import { ResponseProps } from 'services/services.types';

/**
 * Lifecycle of a MarketONE (Amdocs M1) entitlement as reported by the backend.
 *
 * Open string-union so an additive backend status type-checks without a client change. M1 reports
 * these in upper case (`ACTIVE`, `CANCELLED`) — compare through the predicates below rather than
 * against a literal, so a casing change on either side cannot silently flip a benefit's state.
 */
export type MarketoneEntitlementStatus = 'pending' | 'active' | 'cancelled' | (string & {});

export const MARKETONE_CANCELLED_STATUS = 'cancelled';

/**
 * M1 reports statuses in UPPER CASE (`ACTIVE`, `CANCELLED`) while our own fixtures use lower case, so
 * every comparison folds case. Comparing against a literal would read a real `CANCELLED` entitlement
 * as uncancelled and keep discounting a patient the partner has dropped.
 */
export const isCancelledEntitlementStatus = (status?: MarketoneEntitlementStatus): boolean =>
  status?.toLowerCase() === MARKETONE_CANCELLED_STATUS;

/**
 * Whether an entitlement still represents a live partner link.
 *
 * Mirrors the backend's `hasUncancelledEntitlement` (packages/marketone): `CANCELLED` is the only
 * status that revokes the benefit, and a `PENDING` entitlement counts as ours — the gap between
 * PENDING and ACTIVE is our own activation retrying, so reading it as unentitled would show a patient
 * a lapsed benefit while billing still discounts them.
 *
 * Asking for the absence of CANCELLED rather than for a specific status also keeps the open union
 * genuinely open: a status added later is far likelier to mean "still ours" than "no longer".
 */
export const isEntitlementUncancelled = ({ status }: MarketoneEntitlement): boolean =>
  !isCancelledEntitlementStatus(status);
/**
 * One reseller-partner entitlement — today always AT&T, though MarketONE fronts several resellers.
 *
 * ELIGIBILITY IS NEVER DERIVED FROM THESE FIELDS (LMD-1072). `isPartnerBenefitActive` below is the
 * only answer to "does the benefit apply", and it is resolved server-side so no client drifts on
 * clock skew. `status` and `subscriptionEndDate` are read for presentation only — telling the
 * patient *when* a benefit they still hold runs out (LMD-1095) — and every gate, paywall and
 * appointment-flow check must keep reading the flag instead.
 */
export interface MarketoneEntitlement {
  /** When the entitlement was linked to this patient — rendered as `Verified <date>`. */
  activatedAt?: string;
  cancelledAt?: string;
  /** M1 unique id for the partner subscription. Absent on some payloads. */
  partnerSubscriptionId?: string;
  /** Merchant product code the entitlement was provisioned against. */
  productCode?: string;
  status: MarketoneEntitlementStatus;
  /** M1-reported end of the paid period; the benefit is honoured until this instant. */
  subscriptionEndDate?: string;
}

/** Response from `GET /marketone/entitlements` (lifemd PR 17484, LMD-1072). */
export type GetEntitlementsResProps = ResponseProps<{
  entitlements: MarketoneEntitlement[];
  /**
   * Server-resolved answer to "is the reseller-partner benefit in effect right now" — already
   * `true` for a CANCELLED entitlement still inside its `subscriptionEndDate`, because the
   * discount runs to the end of the paid cycle.
   *
   * The single field the client acts on. Never re-derive it from `entitlements[]`, `status` or by
   * comparing dates: the rule lives server-side so every consumer agrees.
   */
  isPartnerBenefitActive: boolean;
}>;

export type BenefitChangeState =
  | 'pending_price_change'
  | 'no_card_ending'
  | 'already_standard'
  | 'benefit_restored'
  | 'not_applicable';

export type BenefitChangePlanVariant = 'weight_management_bundle' | 'membership';

export type BenefitChangeLineItem = {
  after: string;
  before: string;
  key: string;
  label: string;
};

export type BenefitChangePlan = {
  currentPriceCents: number;
  description: string;
  lineItems: BenefitChangeLineItem[];
  name: string;
  standardPriceCents: number;
  variant: BenefitChangePlanVariant;
};

/**
 * Response from GET /marketone/benefit-change (LMD-1130).
 * Clients render from `state` and must not recompute it from dates.
 */
export type GetBenefitChangeResProps = ResponseProps<{
  hasPaymentMethod?: boolean;
  nextBillingAt?: string;
  partner?: { displayName: string };
  plan?: BenefitChangePlan;
  state: BenefitChangeState;
  unlinkedAt?: string;
}>;
